import { Button } from "@/components/ui/button";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { Textarea } from "@/components/ui/textarea";
import { useState } from "react";
import { useMutation } from "convex/react";
import { api } from "../../../../../convex/_generated/api";
import { Id } from "../../../../../convex/_generated/dataModel";
import { Star, Calendar, Building2, Flag, MessageSquare, Loader2 } from "lucide-react";

interface Review {
  _id: Id<"clientReviews">;
  businessId: Id<"businesses">;
  locationId?: Id<"serviceLocations">;
  rating: number;
  comment: string;
  reviewDate: string;
  reviewerName: string;
  reviewerEmail?: string;
  platform: "direct";
  status: "published" | "pending" | "flagged";
  helpful: number;
  unhelpful: number;
  response?: string;
  orgId: Id<"organizations">;
  createdBy: Id<"users">;
  createdAt: number;
  updatedAt: number;
  // Extended fields from query
  clientName: string;
  clientLogo?: string;
  locationName?: string;
  createdByName?: string;
}

interface ViewReviewSheetProps {
  isOpen: boolean;
  onClose: () => void;
  review: Review | null;
}

export function ViewReviewSheet({ isOpen, onClose, review }: ViewReviewSheetProps) {
  const [responseText, setResponseText] = useState(review?.response || "");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isFlagging, setIsFlagging] = useState(false);

  const updateResponse = useMutation(api.clientReviews.updateResponse);
  const updateStatus = useMutation(api.clientReviews.updateStatus);

  if (!review) return null;

  const handleSubmitResponse = async () => {
    if (!responseText.trim()) return;

    setIsSubmitting(true);
    try {
      await updateResponse({
        id: review._id,
        response: responseText.trim(),
      });
      onClose();
    } catch (error) {
      console.error("Failed to submit response:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleFlagReview = async () => {
    setIsFlagging(true);
    try {
      const newStatus = review.status === "flagged" ? "published" : "flagged";
      await updateStatus({
        id: review._id,
        status: newStatus,
      });
      onClose();
    } catch (error) {
      console.error("Failed to update review status:", error);
    } finally {
      setIsFlagging(false);
    }
  };

  return (
    <Sheet open={isOpen} onOpenChange={onClose}>
      <SheetContent className="sm:max-w-md md:max-w-lg">
        <SheetHeader>
          <SheetTitle>Review Details</SheetTitle>
          <SheetDescription>View and respond to client feedback</SheetDescription>
        </SheetHeader>

        <div className="mt-6 space-y-6">
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-full bg-blue-100">
                  <Building2 className="h-5 w-5 text-blue-600" />
                </div>
                <div>
                  <h4 className="font-medium">{review.clientName}</h4>
                  <div className="flex items-center gap-2 text-sm text-gray-500">
                    <div className="flex">
                      {[1, 2, 3, 4, 5].map((star) => (
                        <Star
                          key={star}
                          className={`h-4 w-4 ${star <= review.rating ? "fill-yellow-400 text-yellow-400" : "text-gray-300"}`}
                        />
                      ))}
                    </div>
                  </div>
                </div>
              </div>
              <div>
                {review.status === "flagged" && (
                  <span className="rounded-full bg-red-100 px-2 py-1 text-xs font-medium text-red-800">
                    Flagged
                  </span>
                )}
                {review.status === "pending" && (
                  <span className="rounded-full bg-yellow-100 px-2 py-1 text-xs font-medium text-yellow-800">
                    Pending
                  </span>
                )}
              </div>
            </div>

            <div className="rounded-lg bg-gray-50 p-4">
              <div className="mb-3">
                <p className="text-sm font-medium text-gray-600">Review by {review.reviewerName}</p>
                {review.reviewerEmail && (
                  <p className="text-xs text-gray-500">{review.reviewerEmail}</p>
                )}
              </div>
              <p className="text-gray-700">{review.comment}</p>
              <div className="mt-3 flex items-center justify-end border-t border-gray-200 pt-3">
                <div className="flex items-center gap-1 text-sm text-gray-500">
                  <Calendar className="h-3 w-3" />
                  {review.reviewDate}
                </div>
              </div>
              {review.locationName && (
                <div className="mt-2 border-t border-gray-200 pt-2">
                  <p className="text-xs text-gray-500">Location: {review.locationName}</p>
                </div>
              )}
            </div>

            {review.response && (
              <div className="rounded-lg bg-blue-50 p-4">
                <h5 className="mb-2 text-sm font-medium">Your Response</h5>
                <p className="text-sm text-gray-700">{review.response}</p>
                <div className="mt-2 flex items-center gap-1 text-sm text-gray-500">
                  <Calendar className="h-3 w-3" />
                  {new Date(review.updatedAt).toLocaleDateString()}
                </div>
              </div>
            )}

            <div className="space-y-3">
              <h5 className="font-medium">
                {review.response ? "Update your response" : "Respond to this review"}
              </h5>
              <Textarea
                placeholder="Type your response here..."
                rows={4}
                value={responseText}
                onChange={(e) => setResponseText(e.target.value)}
                disabled={isSubmitting}
              />
              <p className="text-sm text-gray-500">Your response will be visible to the public</p>
            </div>
          </div>

          <div className="flex flex-col gap-3 pt-4 sm:flex-row">
            <Button
              variant="outline"
              className={`gap-2 ${
                review.status === "flagged"
                  ? "text-green-600 hover:bg-green-50 hover:text-green-700"
                  : "text-red-600 hover:bg-red-50 hover:text-red-700"
              }`}
              onClick={handleFlagReview}
              disabled={isSubmitting || isFlagging}
            >
              {isFlagging ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Flag className="h-4 w-4" />
              )}
              {review.status === "flagged" ? "Unflag Review" : "Flag Review"}
            </Button>
            <div className="flex-1"></div>
            <Button
              className="gap-2"
              onClick={handleSubmitResponse}
              disabled={!responseText.trim() || isSubmitting || isFlagging}
            >
              {isSubmitting ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <MessageSquare className="h-4 w-4" />
              )}
              {review.response ? "Update Response" : "Submit Response"}
            </Button>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}
